import { Outlet, useLocation, useParams } from "react-router-dom";
import { Download } from "lucide-react";
import type { BoletoDoPainel } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import {
  useExportarBoletos,
  useListaDeBoletos,
  type FiltrosDeBoletos as FiltrosDaConsulta,
  type ListaQueCresce,
} from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { useAvisos } from "@/componentes/Avisos";
import { Botao } from "@/componentes/Botao";
import { Dica } from "@/componentes/Dica";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { Tabela, type Coluna } from "@/componentes/Tabela";
import { PilulaDeSituacao } from "@/componentes/PilulaDeSituacao";
import { rotaDoBoleto } from "@/rotas";
import { banco, contagem, dia, dinheiro, documento, faz, idCurto } from "@/formato";
import { MarcaDoEnvio } from "./envio";
import { FiltrosDeBoletos, useFiltrosDeBoletos } from "./FiltrosDeBoletos";

/*
 * A tela de Boletos — doc 06 §4.2, e a primeira que o doc 06a §8 manda
 * construir: "é a tela mais usada e a que prova rápido se a densidade, a paleta
 * de estados e a assinatura tipográfica funcionam juntas".
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ OS FILTROS VIVEM NA URL.                                                 │
 * │                                                                          │
 * │ Não é conveniência de implementação: é o que torna um estado de tela     │
 * │ COMUNICÁVEL. O operador que acha algo estranho manda o link no chat, e   │
 * │ o outro vê exatamente a mesma lista. F5 não perde o filtro, e o botão    │
 * │ voltar do navegador faz o que ele promete.                               │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * O detalhe é rota filha (design D7): `Outlet` monta o painel em cima da lista, que segue
 * aqui embaixo, com a mesma rolagem. A linha leva a consulta junto, e o fechar a devolve.
 *
 * Sem seleção por linha nem ação em massa: multiplicar uma ação pela página é o risco que a
 * change evita, e o fluxo de massa é a fila morta.
 */
export function Boletos() {
  const { cliente, estado } = useSessao();
  const { id: aberto } = useParams();
  const { search } = useLocation();

  const filtros = useFiltrosDeBoletos();
  const lista = useListaDeBoletos(cliente, filtros.consulta, estado.fase === "dentro");

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-titulo font-semibold">Boletos</h1>
        {!lista.carregando && <span className="rotulo">{contagemDaLista(lista)}</span>}
        <Exportar filtros={filtros.consulta} />
      </header>

      <FiltrosDeBoletos filtros={filtros} />

      {lista.erro !== null ? (
        <FaixaDeErro erro={lista.erro} />
      ) : (
        <>
          <Tabela
            rotulo="Boletos"
            colunas={COLUNAS}
            itens={lista.itens}
            carregando={lista.carregando}
            chaveDe={(boleto) => boleto.id}
            selecionado={aberto}
            hrefDe={(boleto) => rotaDoBoleto(boleto.id, search)}
            rotuloDe={(boleto) => `Boleto ${boleto.referencia_externa ?? idCurto(boleto.id)}`}
            vazio={filtros.aplicados.length > 0 ? "Nenhum boleto com esses filtros." : "Nenhum boleto."}
          />
          <CarregarMais lista={lista} />
        </>
      )}

      <Outlet context={lista.itens} />
    </section>
  );
}

/** O subtítulo da lista que cresce. Sem total na API, "há mais" é o que a página cheia diz. */
export function contagemDaLista(
  lista: Pick<ListaQueCresce<unknown>, "itens" | "temMais">,
  singular = "boleto",
  plural = "boletos",
): string {
  const texto = contagem(lista.itens.length, singular, plural);
  return lista.temMais ? `${texto} · há mais` : texto;
}

/** A falha da página seguinte fica acima do botão: a lista continua, e ele repete. */
export function CarregarMais({ lista }: { lista: ListaQueCresce<unknown> }) {
  if (!lista.temMais && lista.erroAoCarregarMais === null) return null;

  return (
    <div className="flex flex-col items-center gap-2">
      {lista.erroAoCarregarMais !== null && <FaixaDeErro erro={lista.erroAoCarregarMais} />}
      {lista.temMais && (
        <Botao tom="secundario" carregando={lista.carregandoMais} onClick={lista.carregarMais}>
          Carregar mais
        </Botao>
      )}
    </div>
  );
}

/*
 * O arquivo vem do servidor, e não da página na tela: a rota mascara por papel, audita e
 * conta o corte. O aviso de corte usa o tom de falha porque dura o dobro: quatro segundos
 * são pouco para quem vai confiar na planilha.
 */
function Exportar({ filtros }: { filtros: Omit<FiltrosDaConsulta, "antes"> }) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const exportar = useExportarBoletos(cliente);

  async function baixar() {
    try {
      const { truncado } = await exportar.mutateAsync(filtros);
      if (truncado) avisos.falhou("Arquivo cortado nos 10.000 boletos mais recentes.");
      else avisos.feito("Boletos exportados");
    } catch (falha) {
      avisos.falhou(textoDoErro(falha));
    }
  }

  return (
    <span className="ml-auto flex items-center gap-1">
      <Dica texto="Com os filtros da tela, até 10.000 boletos. Papel de leitura exporta o sacado mascarado." />
      <Botao tom="secundario" carregando={exportar.isPending} onClick={() => void baixar()}>
        <Download aria-hidden="true" className="size-4" />
        Exportar
      </Botao>
    </span>
  );
}

/*
 * As oito colunas do doc 06a §4.2, nesta ordem.
 *
 * Larguras FIXAS em `grid-template-columns`: coluna que se ajusta ao conteúdo
 * dança a cada atualização do polling, e uma tabela que dança a cada trinta
 * segundos é impossível de varrer com o olho.
 *
 * Em 1024 ficam situação, referência, sacado, valor e vencimento, que é o que responde
 * "por que esse boleto está assim". O resto está no painel de detalhe.
 */
export const COLUNAS_ESSENCIAIS: readonly Coluna<BoletoDoPainel>[] = [
  {
    chave: "situacao",
    rotulo: "Situação",
    largura: "132px",
    render: (boleto) => (
      <PilulaDeSituacao situacao={boleto.situacao} indeterminado={boleto.indeterminado} />
    ),
  },
  {
    chave: "referencia",
    rotulo: "Referência",
    largura: "minmax(120px, 1fr)",
    render: (boleto) => (
      <span className="flex min-w-0 items-center gap-1">
        {/*
          Aqui, e não ao lado da pílula: a situação do envio incerto é verdadeira, e a coluna
          dela tem largura fixa.
        */}
        <MarcaDoEnvio estado={boleto.envio_estado} />
        <span className="dados truncate" title={boleto.referencia_externa ?? boleto.id}>
          {boleto.referencia_externa ?? "—"}
        </span>
      </span>
    ),
  },
  {
    chave: "sacado",
    rotulo: "Sacado",
    largura: "minmax(150px, 1.4fr)",
    render: (boleto) => (
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate">{boleto.sacado_nome || "—"}</span>
        <span className="dados truncate text-[11px] text-texto-3">
          {documento(boleto.sacado_documento)}
        </span>
      </div>
    ),
  },
  {
    chave: "valor",
    rotulo: "Valor",
    largura: "120px",
    direita: true,
    // Dígitos tabulares: sem isso as colunas de dinheiro desalinham linha a
    // linha, e comparar 2.300,00 com 230,00 vira trabalho de contar caractere.
    render: (boleto) => <span className="tabular">{dinheiro(boleto.valor_centavos)}</span>,
  },
  {
    chave: "vencimento",
    rotulo: "Vencimento",
    largura: "104px",
    direita: true,
    render: (boleto) => <span className="tabular">{dia(boleto.vencimento)}</span>,
  },
];

const COLUNAS: readonly Coluna<BoletoDoPainel>[] = [
  ...COLUNAS_ESSENCIAIS,
  {
    chave: "banco",
    rotulo: "Banco",
    largura: "64px",
    descarte: 1,
    render: (boleto) => (
      // `z-[1]` sobe acima da camada do link da linha: embaixo dela, o `title` nunca abre.
      <span className="dados relative z-[1] text-texto-2" title={banco(boleto.banco)}>
        {boleto.banco}
      </span>
    ),
  },
  {
    chave: "sistema",
    rotulo: "Aplicação",
    largura: "minmax(110px, 1fr)",
    descarte: 3,
    render: (boleto) => <span className="truncate text-texto-2">{boleto.sistema_nome || "—"}</span>,
  },
  {
    chave: "atualizado",
    rotulo: "Atualizado",
    largura: "84px",
    descarte: 2,
    direita: true,
    render: (boleto) => (
      <span className="tabular text-texto-2" title={boleto.atualizado_em}>
        {faz(boleto.atualizado_em)}
      </span>
    ),
  },
];
